import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toSTL, toOBJ, toJSON, downloadText, downloadDataURL } from '@/lib/exporters';
import { buildPanelSheet } from '@/lib/panelSheet';
import { renderDXF } from '@/lib/panelSheetDXF';
import { CURVED_TEMPLATES } from '@/lib/polyhedra';
import { useStudio } from '@/lib/store';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import axios from 'axios';
import { FileDown, Image as ImageIcon, Save, Braces, Scissors, ChevronDown, Lock } from 'lucide-react';
import PanelSheetPreview from './PanelSheetPreview';
import { SendToFreehandCard } from './SendToFreehand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportDialog({ open, onOpenChange, captureFn, onNeedsUpgrade }) {
  const [name, setName] = useState('My Panel Design');
  const [advanced, setAdvanced] = useState(false);
  const [ballDiameter, setBallDiameter] = useState(213.2);
  const [seamAllowance, setSeamAllowance] = useState(2.5);
  const [holeDiameter, setHoleDiameter] = useState(1);
  const [holeSpacing, setHoleSpacing] = useState(4);
  const [holeStyle, setHoleStyle] = useState('round'); // 'round' | 'slit'
  const [slitLength, setSlitLength] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const state = useStudio();
  const { isPro } = useAuth();
  const guardPro = () => {
    if (isPro) return true;
    onNeedsUpgrade && onNeedsUpgrade();
    return false;
  };

  const handleSTL = () => {
    if (!guardPro()) return;
    const text = toSTL(state.vertices, state.faces, 1.25);
    downloadText(`${slug(name)}.stl`, text, 'model/stl');
    toast.success('STL exported');
  };
  const handleOBJ = () => {
    if (!guardPro()) return;
    const text = toOBJ(state.vertices, state.faces, 1.25);
    downloadText(`${slug(name)}.obj`, text, 'text/plain');
    toast.success('OBJ exported');
  };
  const handleJSON = () => {
    if (!guardPro()) return;
    const design = designPayload(name, state);
    downloadText(`${slug(name)}.json`, toJSON(design), 'application/json');
    toast.success('JSON exported');
  };
  const handlePNG = () => {
    if (!guardPro()) return;
    if (!captureFn) return toast.error('Canvas not ready');
    const dataURL = captureFn();
    downloadDataURL(`${slug(name)}.png`, dataURL);
    toast.success('PNG exported');
  };
  const handleSVG = (overrides) => {
    if (!guardPro()) return;
    try {
      const sheetWidthMM = 300;
      const spherized = CURVED_TEMPLATES.has(state.templateId);
      const opts = {
        ballDiameterMM: Number(ballDiameter),
        seamAllowanceMM: Number(seamAllowance),
        holeDiameterMM: Number(holeDiameter),
        holeSpacingMM: Number(holeSpacing),
        holeStyle,
        slitLengthMM: Number(slitLength),
        foamInsetMM: 3, // regulation preset: yellow foam-liner cut line
        ...(overrides || {}),
      };
      const { svg, panels, sheetHeightMM } = buildPanelSheet(state.vertices, state.faces, {
        ...opts,
        sheetWidthMM,
        spherized,
        smoothVertices: state.smoothVertices || [],
      });
      const suffix = overrides?.presetSlug ? `-${overrides.presetSlug}` : '-panels';
      setPreviewData({
        svg,
        filename: `${slug(name)}${suffix}.svg`,
        meta: { panels, sheetHeightMM, sheetWidthMM, spherized, ...opts },
      });
      setPreviewOpen(true);
    } catch (e) {
      console.error(e);
      toast.error('Panel sheet generation failed');
    }
  };
  const handleHackySack = () =>
    handleSVG({
      ballDiameterMM: 50.8,   // 2 inches
      seamAllowanceMM: 3,     // thicker seam for hand-stitched leather
      holeDiameterMM: 1,
      holeSpacingMM: 3,
      foamInsetMM: 0,         // no foam liner for hacky sack
      presetSlug: 'hacky-sack',
    });

  // DXF download — shares the panel-sheet layout with the SVG preview so
  // the physical dimensions match exactly. No preview UI (DXF is a CAD
  // text format); triggers a direct download.
  const handleDXF = (overrides) => {
    if (!guardPro()) return;
    try {
      const sheetWidthMM = 300;
      const spherized = CURVED_TEMPLATES.has(state.templateId);
      const opts = {
        ballDiameterMM: Number(ballDiameter),
        seamAllowanceMM: Number(seamAllowance),
        holeDiameterMM: Number(holeDiameter),
        holeSpacingMM: Number(holeSpacing),
        holeStyle,
        slitLengthMM: Number(slitLength),
        foamInsetMM: 3,
        ...(overrides || {}),
      };
      const { laid, renderOpts } = buildPanelSheet(state.vertices, state.faces, {
        ...opts,
        sheetWidthMM,
        spherized,
        smoothVertices: state.smoothVertices || [],
      });
      const dxf = renderDXF(laid, renderOpts);
      const suffix = overrides?.presetSlug ? `-${overrides.presetSlug}` : '-panels';
      downloadText(`${slug(name)}${suffix}.dxf`, dxf, 'application/dxf');
      toast.success('DXF exported');
    } catch (e) {
      console.error(e);
      toast.error('DXF export failed');
    }
  };
  const handleDXFHackySack = () =>
    handleDXF({
      ballDiameterMM: 50.8,
      seamAllowanceMM: 3,
      holeDiameterMM: 1,
      holeSpacingMM: 3,
      foamInsetMM: 0,
      presetSlug: 'hacky-sack',
    });
  const [overwriteConfirm, setOverwriteConfirm] = useState(null);

  const persistSave = async (payload, overwriting = false) => {
    try {
      await axios.post(`${API}/designs`, payload);
      toast.success(overwriting ? `Overwrote "${payload.name}"` : 'Saved to gallery');
      onOpenChange(false);
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const handleSave = async () => {
    if (!guardPro()) return;
    const thumb = captureFn ? captureFn() : null;
    const payload = { ...designPayload(name, state), thumbnail: thumb };
    // Detect an existing gallery entry with the same name so we can prompt
    // the user before the backend silently overwrites it.
    try {
      const { data } = await axios.get(`${API}/designs`);
      if (Array.isArray(data) && data.some((d) => d.name === payload.name)) {
        setOverwriteConfirm(payload);
        return;
      }
    } catch {
      // Auth/network hiccup — let the POST surface the real error.
    }
    persistSave(payload, false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight flex items-center gap-2">
            Export design
            {!isPro && (
              <span className="text-[10px] font-semibold uppercase tracking-widest bg-slate-900 text-white rounded-full px-2 py-0.5" data-testid="export-locked-badge">
                Pro · $9
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isPro
              ? 'Download a 3D file, a real-scale panel sheet for cutting, or save to your gallery.'
              : 'Every option below is locked until you unlock Pro. Preview the studio freely — one $9 upgrade enables all exports forever.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Design name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="export-name-input"
              className="rounded-xl"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <ExportBtn testId="btn-export-stl" onClick={handleSTL} icon={<FileDown className="w-4 h-4" />} locked={!isPro}>
              STL
              <span className="block text-[10px] text-slate-500">3D printable</span>
            </ExportBtn>
            <ExportBtn testId="btn-export-obj" onClick={handleOBJ} icon={<FileDown className="w-4 h-4" />} locked={!isPro}>
              OBJ
              <span className="block text-[10px] text-slate-500">3D mesh</span>
            </ExportBtn>
            <ExportBtn testId="btn-export-png" onClick={handlePNG} icon={<ImageIcon className="w-4 h-4" />} locked={!isPro}>
              PNG
              <span className="block text-[10px] text-slate-500">Studio snapshot</span>
            </ExportBtn>
            <ExportBtn testId="btn-export-json" onClick={handleJSON} icon={<Braces className="w-4 h-4" />} locked={!isPro}>
              JSON
              <span className="block text-[10px] text-slate-500">Design data</span>
            </ExportBtn>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
            <div className="flex gap-2">
              <button
                onClick={() => handleSVG()}
                data-testid="btn-export-svg"
                className="flex-1 rounded-lg bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 p-3 text-left transition-colors duration-200 flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-lg bg-[#111827] text-white flex items-center justify-center shrink-0">
                  {isPro ? <Scissors className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1">
                    Regulation ball (SVG)
                    {!isPro && <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Pro</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                    Flatten all panels to real-scale 2D · Ø{ballDiameter}mm ball · +{seamAllowance}mm seam
                    allowance · Ø{holeDiameter}mm stitch holes every {holeSpacing}mm · yellow foam-liner
                    cut (3mm inset from stitch line)
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleDXF()}
                data-testid="btn-export-dxf"
                title="Download DXF (laser-cutter format)"
                className="shrink-0 w-16 rounded-lg bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 p-2 flex flex-col items-center justify-center gap-1 text-slate-900"
              >
                {isPro ? <FileDown className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                <span className="text-[11px] font-semibold tracking-wide">DXF</span>
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleHackySack}
                data-testid="btn-export-svg-hackysack"
                className="flex-1 rounded-lg bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 p-3 text-left transition-colors duration-200 flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded-lg bg-amber-600 text-white flex items-center justify-center shrink-0">
                  {isPro ? <Scissors className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1">
                    Hacky sack — 2″ (SVG)
                    {!isPro && <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Pro</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                    Miniature hand-stitched preset · Ø50.8mm ball · +3mm seam allowance · Ø1mm
                    stitch holes every 3mm — sized for suede/leather sacks
                  </div>
                </div>
              </button>
              <button
                onClick={handleDXFHackySack}
                data-testid="btn-export-dxf-hackysack"
                title="Download hacky-sack DXF"
                className="shrink-0 w-16 rounded-lg bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 p-2 flex flex-col items-center justify-center gap-1 text-slate-900"
              >
                {isPro ? <FileDown className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                <span className="text-[11px] font-semibold tracking-wide">DXF</span>
              </button>
            </div>
            <button
              onClick={() => setAdvanced((v) => !v)}
              data-testid="btn-advanced-toggle"
              className="mt-2 text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
            >
              <ChevronDown className={`w-3 h-3 transition-transform ${advanced ? 'rotate-180' : ''}`} />
              {advanced ? 'Hide' : 'Adjust'} regulation dimensions
            </button>
            {advanced && (
              <div className="mt-3 space-y-3" data-testid="advanced-dimensions">
                <div className="grid grid-cols-2 gap-2">
                  <NumField label="Ball Ø (mm)" value={ballDiameter} onChange={setBallDiameter} testId="input-ball-diameter" step="0.1" />
                  <NumField label="Seam allowance (mm)" value={seamAllowance} onChange={setSeamAllowance} testId="input-seam-allowance" step="0.1" />
                  {holeStyle === 'round' ? (
                    <NumField label="Hole Ø (mm)" value={holeDiameter} onChange={setHoleDiameter} testId="input-hole-diameter" step="0.1" />
                  ) : (
                    <NumField label="Slit length (mm)" value={slitLength} onChange={setSlitLength} testId="input-slit-length" step="0.1" />
                  )}
                  <NumField label="Hole spacing (mm)" value={holeSpacing} onChange={setHoleSpacing} testId="input-hole-spacing" step="0.1" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Hole style</div>
                  <div className="grid grid-cols-2 gap-1.5" data-testid="hole-style-group">
                    <button
                      type="button"
                      onClick={() => setHoleStyle('round')}
                      data-testid="hole-style-round"
                      className={`text-[11px] rounded-lg px-2 py-1.5 border transition-colors ${holeStyle === 'round' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'}`}
                    >
                      Round
                      <div className={`text-[10px] mt-0.5 ${holeStyle === 'round' ? 'text-slate-300' : 'text-slate-500'}`}>Drill-friendly circles</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setHoleStyle('slit')}
                      data-testid="hole-style-slit"
                      className={`text-[11px] rounded-lg px-2 py-1.5 border transition-colors ${holeStyle === 'slit' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'}`}
                    >
                      Awl slits (45°)
                      <div className={`text-[10px] mt-0.5 ${holeStyle === 'slit' ? 'text-slate-300' : 'text-slate-500'}`}>Hand-pricked saddler's seam</div>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={handleSave}
            className="w-full rounded-xl bg-slate-900 hover:bg-slate-800 text-white h-11"
            data-testid="btn-save-gallery"
          >
            {isPro ? <Save className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
            {isPro ? 'Save to Gallery' : 'Save to Gallery — Pro'}
          </Button>

          <SendToFreehandCard onBeforeHandoff={() => onOpenChange(false)} />
        </div>
      </DialogContent>
    </Dialog>
    <PanelSheetPreview
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      svg={previewData?.svg}
      meta={previewData?.meta}
      filename={previewData?.filename || 'panels.svg'}
    />
    {/* Overwrite confirmation — fires only when the user tries to save
        with a name that already exists in their gallery. */}
    <Dialog open={!!overwriteConfirm} onOpenChange={(o) => !o && setOverwriteConfirm(null)}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="export-overwrite-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight">Overwrite existing design?</DialogTitle>
          <DialogDescription>
            A design named <span className="font-semibold text-slate-900">“{overwriteConfirm?.name}”</span> already
            exists in your gallery. Saving will replace it — this can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOverwriteConfirm(null)}
            data-testid="export-overwrite-cancel"
          >
            Keep both — rename
          </Button>
          <Button
            onClick={async () => {
              const p = overwriteConfirm;
              setOverwriteConfirm(null);
              if (p) await persistSave(p, true);
            }}
            data-testid="export-overwrite-confirm"
            className="rounded-full bg-red-600 hover:bg-red-700 text-white"
          >
            Overwrite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function NumField({ label, value, onChange, testId, step = '1' }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-slate-500 mb-1">{label}</span>
      <Input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="rounded-lg h-9 text-sm bg-white"
      />
    </label>
  );
}

function ExportBtn({ children, onClick, icon, testId, locked }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative rounded-xl border p-3 text-left transition-colors duration-200 ${
        locked
          ? 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
        {locked ? <Lock className="w-4 h-4 text-slate-500" /> : icon}
        {children}
      </div>
    </button>
  );
}

function slug(s) {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'design';
}

function designPayload(name, s) {
  return {
    name,
    template: s.templateId,
    panel_count: s.faces.length,
    vertices: s.vertices,
    faces: s.faces,
    colors: s.colors,
    seam_width: s.seamWidth,
    finish: s.finish,
    smooth_vertices: s.smoothVertices || [],
  };
}
